import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/auth-context";
import { authedFetch } from "@/lib/authed-fetch";
import { setPulseWorkbenchIntent } from "@/lib/pulse-workbench-intent";
import { useToast } from "@/hooks/use-toast";

const PENDING_KEY = "acolori.calendarDeepLink.v1";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PROJECT_RE = /^[A-Za-z0-9_-]{7}$/;

export type CalendarDeepLink =
  | { type: "event"; eventId: string }
  | { type: "pulse"; projectId: string; pulseId: number };

export function parseCalendarDeepLink(search: string): CalendarDeepLink | null {
  const params = new URLSearchParams(search);
  const eventId = params.get("calendarEvent");
  const projectId = params.get("calendarProject");
  const rawPulseId = params.get("calendarPulse");
  const eventCount = params.getAll("calendarEvent").length;
  const projectCount = params.getAll("calendarProject").length;
  const pulseCount = params.getAll("calendarPulse").length;
  if (eventCount === 1 && projectCount === 0 && pulseCount === 0 && eventId && UUID_RE.test(eventId)) {
    return { type: "event", eventId };
  }
  if (
    eventCount === 0
    && projectCount === 1
    && pulseCount === 1
    && projectId
    && rawPulseId
    && PROJECT_RE.test(projectId)
    && /^\d{1,10}$/.test(rawPulseId)
  ) {
    const pulseId = Number(rawPulseId);
    if (Number.isSafeInteger(pulseId) && pulseId > 0) {
      return { type: "pulse", projectId, pulseId };
    }
  }
  return null;
}

function hasCalendarParams(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.has("calendarEvent") || params.has("calendarProject") || params.has("calendarPulse");
}

export function stripCalendarDeepLink(url: URL): string {
  const params = new URLSearchParams(url.search);
  params.delete("calendarEvent");
  params.delete("calendarProject");
  params.delete("calendarPulse");
  const query = params.toString();
  return `${url.pathname}${query ? `?${query}` : ""}${url.hash}`;
}

function loadPending(): CalendarDeepLink | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (value?.type === "event" && typeof value.eventId === "string" && UUID_RE.test(value.eventId)) {
      return value;
    }
    if (
      value?.type === "pulse"
      && typeof value.projectId === "string"
      && PROJECT_RE.test(value.projectId)
      && Number.isSafeInteger(value.pulseId)
      && value.pulseId > 0
    ) {
      return value;
    }
  } catch {
    // Corrupt browser state is discarded below.
  }
  sessionStorage.removeItem(PENDING_KEY);
  return null;
}

function savePending(link: CalendarDeepLink) {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(link));
}

function clearPending() {
  sessionStorage.removeItem(PENDING_KEY);
}

/**
 * Handles only the two allowlisted authenticated calendar destinations. It
 * never follows a server-provided URL and never invokes a call start action.
 */
export function CalendarDeepLinkHandler() {
  const { isAuthenticated, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const processingRef = useRef(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  const captureLocation = useCallback(() => {
    const link = parseCalendarDeepLink(window.location.search);
    if (link) savePending(link);
    if (hasCalendarParams(window.location.search)) {
      window.history.replaceState(window.history.state, "", stripCalendarDeepLink(new URL(window.location.href)));
    }
    return link;
  }, []);

  // Capture before ProtectedRoute can replace the location with /login.
  useLayoutEffect(() => {
    captureLocation();
  }, [captureLocation, historyVersion]);

  useEffect(() => {
    const onPopState = () => setHistoryVersion((version) => version + 1);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (isLoading || !isAuthenticated || processingRef.current) return;
    const link = loadPending();
    if (!link) return;
    processingRef.current = true;
    let cancelled = false;

    const fail = (message: string) => {
      if (cancelled) return;
      clearPending();
      toast({ title: "Calendar link unavailable", description: message, variant: "destructive" });
      window.dispatchEvent(new CustomEvent("calendar-deep-link-error", { detail: { message } }));
    };

    void (async () => {
      try {
        if (link.type === "event") {
          const response = await authedFetch(`/api/calendar/events/${encodeURIComponent(link.eventId)}`);
          if (!response.ok) {
            fail(response.status === 403 || response.status === 404
              ? "You do not have access to this calendar event."
              : `The calendar event could not be opened (${response.status}).`);
            return;
          }
          const body = await response.json();
          if (cancelled) return;
          clearPending();
          if (window.location.pathname !== "/") navigate("/");
          requestAnimationFrame(() => {
            window.dispatchEvent(new CustomEvent("open-calendar-event", {
              detail: { eventId: link.eventId, item: body?.item ?? body },
            }));
          });
          return;
        }

        const response = await authedFetch(`/api/pulses/${link.pulseId}/workbench`);
        if (!response.ok) {
          fail(response.status === 403 || response.status === 404
            ? "You do not have access to this calendar pulse."
            : `The calendar pulse could not be opened (${response.status}).`);
          return;
        }
        const body = await response.json();
        if (String(body?.projectId ?? "") !== link.projectId) {
          fail("This calendar link does not match the requested project.");
          return;
        }
        if (cancelled) return;
        clearPending();
        setPulseWorkbenchIntent({ pulseId: link.pulseId });
        navigate(`/?projectId=${encodeURIComponent(link.projectId)}`);
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent("navigate-to-project", {
            detail: { projectId: link.projectId },
          }));
          window.dispatchEvent(new CustomEvent("open-pulse-workbench", {
            detail: { pulseId: link.pulseId },
          }));
        });
      } catch {
        fail("The calendar link could not be verified. Please try again.");
      } finally {
        processingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [historyVersion, isAuthenticated, isLoading, navigate, toast]);

  return null;
}